import React, { Component } from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import classNames from 'classnames'
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl'
import { Container, Row, Col } from 'react-grid-system'
import { delay } from 'bluebird'

import {
  DealerC,
  EMessageType,
  darkThemeName,
  UniMessage,
  UniButton,
  UniImg,
  UniWorkflow,
  UniInput,
  UniOverlapGroup,
  UniOverlapButton,
  UniLocalize,
  UniConditionalRender,
  InviteC,
  Editable,
  EInviteStatusC,
  AuthUserC,
  IUniToast_Alert,
  notBlankV10n, commPasswordRequirementsV10n, passwordMatchV10n,
  UniLoader,
  IRouteMatchParamsHOCProps,
  withRouteMatchParams
} from '@unikey/unikey-commons/release/comm';

import partnerConfig from '@alias-current-partner-customizations';
import {
  api,
  addAlert,
  FooterContainer,
  attemptRetrieveAuthUser,
  attemptRetrieveAuthUserOptional,
  attemptAcceptAdminInviteAsExistingUser,
  attemptRetrieveAdminInviteByToken,
  setActivePortalDealer,
  PartnerCustomizations, IPartnerCustomizations,
  partnerKey,
  redirectToLogin,
  navConfig,
  ENavPages,
  IAcceptInviteActionParams,
  requestRefreshToken,
  addPendingRedirect,
  IPendingRedirectActionParams,
  IRedirectOptions,
  setPendingAdminInvitePath,
  getPendingAdminInvitePath,
  clearPendingAdminInvitePath,
} from '../internal';


interface IProps extends WrappedComponentProps, IPartnerCustomizations, IRouteMatchParamsHOCProps {
  firstName: Editable<string>,
  lastName: Editable<string>,
  password: Editable<string>,
  passwordConfirm: Editable<string>,
  firstNameValid: boolean,
  lastNameValid: boolean,
  passwordValid: boolean,
  passwordConfirmValid: boolean,
  accepting: boolean,
  inviteData: InviteC,
  validCaptcha: boolean,
  darkModeTheme: string,
  authUser: AuthUserC,
  authUserLoading?: boolean,
  getCurrentUserOptional(ignoreCache: boolean): Promise<AuthUserC>,
  addPendingRedirect(redirOpts: IPendingRedirectActionParams): void,
  redirectToLoginWithContinue(options?: IRedirectOptions): void,
  acceptInvite(params: IAcceptInviteActionParams): Promise<void>,
  handlePasswordChange(change: Editable): void,
  handlePasswordConfirmChange(change: Editable): void,
  handleFirstNameChange(change: Editable): void,
  handleLastNameChange(change: Editable): void,
  getInvitationDetails(token: string): void,
  setActivePortalDealer(dealer: DealerC, switchingDealers?: boolean): void,
  addAlert(config: IUniToast_Alert): void,
}

class AdminInvitationAcceptForm extends Component<IProps> {

  constructor(props: IProps) {
    super(props);
  }

  componentDidMount() {
    this.props.getCurrentUserOptional(true);
    if (!this.props.inviteData.inviterEmail) {
      this.props.getInvitationDetails(decodeURIComponent(this.props.match.params.inviteToken!));
    }
    if (this.props.inviteData.status === EInviteStatusC.expired) {
      this.props.addAlert({
        id: Date.now(),
        titleKey: 'invitationExpired',
        type: EMessageType.error,
        messageKeys: ['invitationHasExpired']
      })
    } else if (this.props.inviteData.status === EInviteStatusC.revoked || this.props.inviteData.status === EInviteStatusC.accepted || this.props.inviteData.status === EInviteStatusC.declined) {
      this.props.addAlert({
        id: Date.now(),
        titleKey: 'inviteNotAvailable',
        type: EMessageType.error,
        messageKeys: ['inviteNoLongerValid']
      })
    }
  }

  componentDidUpdate() {
    // once an authenticated user has landed here, the pending invite has been delivered
    // and should no longer redirect them back to this page on future token refreshes.
    if (!this.props.authUserLoading && this.props.authUser?.id && getPendingAdminInvitePath()) {
      clearPendingAdminInvitePath();
    }
  }

  _acceptInvite = async () => {
    await this.props.acceptInvite({
      token: this.props.match.params.inviteToken,
      dealerId: this.props.inviteData.dealer!.id!
    })
    
    await delay(1000);

    // then refresh the access token and reload the user after the dealer is set
    // await requestRefreshToken(true, this.props.refreshSuccess);
    await requestRefreshToken(true);
    const authUser = await this.props.getCurrentUserOptional(true);
    // load the dealer into redux and redirect to the portal landing page with that dealer set as the active dealer.
    const dealer = authUser.getPreferredDealer(api.cacheImpl);
    if (dealer) {
      this.props.setActivePortalDealer(dealer, true);
    }
    this.props.addPendingRedirect({ pathname: navConfig.get(ENavPages.portalLanding)!.linkTo([]) });

  }

  _goToLoginWithContinueHere = () => {
    // match.params comes back decoded, so must re-encode to preserve redirecting with token in routes
    // without considering slashes in tokes the next segment of the route
    const encodedAcceptToken = encodeURIComponent(this.props.match.params.inviteToken!);
    const acceptPath = navConfig.get(ENavPages.adminInviteAccept)!.linkTo([encodedAcceptToken]);
    // remember the invite independently of `continueTo` so it survives registration and email verification
    setPendingAdminInvitePath(acceptPath);
    this.props.redirectToLoginWithContinue({
      preventContinue: false,
      continuePath: acceptPath
    })
  }

  render() {
    if (this.props.render) {
      return this.props.render();
    }
    const imgClassName = classNames('partner-logo', {
      [partnerKey]: true
    });

    const authUserBelongsToInvitedDealer = this.props.authUser?.dealers?.some(d => d?.id === this.props.inviteData.dealer?.id) ?? false;
    const formValid: boolean = this.props.inviteData.status < EInviteStatusC.accepted && this.props.firstNameValid && this.props.lastNameValid && this.props.passwordValid && this.props.passwordConfirmValid;
    return (
      <section className='access-container'>
        <section className='admin-register-container'>
          {/* access flow should conform to max width restrictions for massive screens */}
          <Container>
            <UniImg textKey="partner logo" className={imgClassName} src={this.props.darkModeTheme === darkThemeName ? partnerConfig.assets.logoOnDark : partnerConfig.assets.logoOnBackground} />
            <UniWorkflow
              className="accept-invite-workflow"
              titleKey="acceptAdminInvite"
              titleIcon="accountCircle"
              size="wider">
              <UniConditionalRender visible={this.props.inviteData.status < EInviteStatusC.accepted} >
                {/* not logged in */}
                <UniConditionalRender visible={!this.props.authUser?.id}>
                  <UniLocalize translate="signInToYourAccountToAcceptEKeys"/>
                </UniConditionalRender>
                
                {/* accept message */}
                <UniConditionalRender visible={!!this.props.authUser?.id}>
                  <p><UniLocalize translate="_adminAcceptMessage" /></p>
                </UniConditionalRender>
              </UniConditionalRender>

              <UniConditionalRender visible={this.props.inviteData.status >= EInviteStatusC.accepted}>
                <UniMessage 
                  type={EMessageType.warn}
                  titleKey="inviteInvalid"
                  messageKeys={['_adminInviteInvalidMessage']} />
              </UniConditionalRender>

              {/* Ok to not use editable since this is disabled always */}
              <UniInput
                value={this.props.inviteData.inviterEmail}
                labelKey="invitedBy"
                placeholderKey="email"
                disabled={true} />
              <UniInput
                value={this.props.inviteData.inviteeEmail}
                labelKey="sentTo"
                placeholderKey="sentTo"
                disabled={true} />

              <UniConditionalRender visible={!!this.props.inviteData.dealer?.name}>
                <UniInput
                  value={this.props.inviteData.dealer?.name?.value}
                  labelKey="dealerName"
                  placeholderKey="dealerName"
                  disabled={true} />
              </UniConditionalRender>

            </UniWorkflow>

            <UniConditionalRender visible={!this.props.authUserLoading}>
              <UniConditionalRender visible={this.props.inviteData.status < EInviteStatusC.accepted && !authUserBelongsToInvitedDealer}>
                    
                {/* user is not logged in */}
                <UniConditionalRender visible={!this.props.authUser?.id}>
                  <Row>
                    <Col xs={24} md={16} xl={12} push={{md: 4, xl: 6}}>
                      <UniButton
                        className="admin-invite-accept-button"
                        handleClick={() => this._goToLoginWithContinueHere() }
                        textKey="signInToAccept"
                        tooltipPosition="top" />
                    </Col>
                  </Row>
                
                  <Row>
                    <Col xs={24}>
                      <br/>
                      <p className="text-center"><UniLocalize translate="dontHaveAnAccount" /></p>
                    </Col>

                    <Col xs={24} md={16} xl={12} push={{md: 4, xl: 6}}>
                      <UniButton
                        className="admin-invite-accept-button"
                        handleClick={() => this._goToLoginWithContinueHere() }
                        textKey="registerToAccept"
                        theme="secondary"
                        tooltipPosition="top" />
                    </Col>
                  </Row>
                </UniConditionalRender>

                {/* user is logged in and not the sender */}
                <UniConditionalRender visible={!!this.props.authUser?.id}>
                  <Row>
                    <Col xs={24} md={16} xl={12} push={{md: 4, xl: 6}}>
                      <UniButton
                        className="admin-invite-accept-button"
                        handleClick={this._acceptInvite}
                        textKey={this.props.intl.formatMessage(
                          { id: '_acceptInvitationAs[userFullName]' }, 
                          { 'userFullName': this.props.authUser?.getFullName?.() || this.props.authUser?.username || '' }
                        )}
                        disabled={this.props.accepting}
                        tempDisableOnClick={true}
                        disabledReasonKeys={['cannotAcceptFromTheSenderAccount']}
                        showLoader={this.props.accepting}
                        tooltipPosition="top" >
                          <br/>
                          <strong>
                            <UniLocalize
                              translate="_acceptAs[userFullName][username]" 
                              variables={{
                                'userFullName': this.props.authUser?.getFullName?.() || this.props.authUser?.username || '',
                                'username': this.props.authUser?.username
                              }} />
                          </strong>
                      </UniButton>
                    </Col>
                  </Row>

                  <Row>
                  <Col xs={24} md={16} xl={12} push={{md: 4, xl: 6}}>
                      <UniButton
                        className="admin-invite-accept-button"
                        textKey={this.props.intl.formatMessage(
                          { id: '_not[userFullName]acceptInDifferentAccount' }, 
                          { 'userFullName': this.props.authUser?.getFullName?.() || this.props.authUser?.username || '' }
                        )}
                        theme="inverted"
                        handleClick={() => this._goToLoginWithContinueHere() } />
                    </Col>
                  </Row>

                  <Row>
                  <Col xs={24} md={16} xl={12} push={{md: 4, xl: 6}}>
                      <UniButton
                        className="admin-invite-accept-button"
                        handleClick={() => this.props.addPendingRedirect({ pathname: navConfig.get(ENavPages.organizations)!.linkTo() })}
                        textKey="_continueToPortalWithoutAccepting"
                        theme="inverted" />
                    </Col>
                  </Row>
                </UniConditionalRender>

              </UniConditionalRender>

              <UniConditionalRender visible={this.props.inviteData.status === EInviteStatusC.expired}>
                <h4 className="text-center">
                  <UniLocalize translate="invitationExpiredContactSenderToResend" />
                </h4>

                <Row>
                <Col xs={24} md={16} xl={12} push={{md: 4, xl: 6}}>
                    <UniButton
                      className="admin-invite-accept-button"
                      handleClick={this._acceptInvite}
                      textKey="acceptNewEKeys"
                      disabled={true}
                      disabledReasonKeys={['sharedEkeysExpired', 'cannotAcceptExpiredEKeys']}
                      tooltipPosition="right" />
                  </Col>
                </Row>
              </UniConditionalRender>

              <UniConditionalRender visible={authUserBelongsToInvitedDealer}>
                <h4 className="text-center">
                  <UniLocalize translate="alreadyMemberOfInvitedDealer" />
                </h4>

                <Row>
                <Col xs={24} md={16} xl={12} push={{md: 4, xl: 6}}>
                    <UniButton
                      className="admin-invite-accept-button"
                      textKey="acceptInDifferentAccount"
                      theme="secondary"
                      handleClick={() => this._goToLoginWithContinueHere() } />
                  </Col>
                </Row>

                <Row>
                  <Col xs={24} md={16} xl={12} push={{md: 4, xl: 6}}>
                    <UniButton
                      className="admin-invite-accept-button"
                      handleClick={() => this.props.addPendingRedirect({ pathname: navConfig.get(ENavPages.organizations)!.linkTo() })}
                      textKey="_continueToPortalWithoutAccepting"
                      theme="inverted" />
                  </Col>
                </Row>
              </UniConditionalRender>

            </UniConditionalRender>
            {/* new stuff */}

          </Container>
        </section>
        <FooterContainer />
      </section>
    )
  }
}

function mapStateToProps(state: any) {
  return {
    authUserLoading: state.authenticatedUser.loading,
    authUser: state.authenticatedUser.currentUser,
    inviteData: state.inviteDetails.inviteData,
    accepting: state.adminRegisterForm.loading,
    darkModeTheme: state.portal.darkModeTheme
  };
}

const mapDispatchToProps = (dispatch: any) => bindActionCreators({
  getCurrentUserOptional: attemptRetrieveAuthUserOptional,
  getInvitationDetails: attemptRetrieveAdminInviteByToken,
  acceptInvite: attemptAcceptAdminInviteAsExistingUser,
  redirectToLoginWithContinue: redirectToLogin,
  // refreshSuccess: oidcRefreshSuccess,
  setActivePortalDealer,
  addAlert,
  addPendingRedirect,
}, dispatch);

export default withRouteMatchParams(
  connect(mapStateToProps, mapDispatchToProps)(
    injectIntl(AdminInvitationAcceptForm, { forwardRef: true })
  )
)
